// Maps the XBRL tags Companies House filings actually use onto the line-item
// order set out in the Companies Act 2006 (SI 2008/410, Schedule 1, Format 1)
// balance sheet and profit & loss account. Tag names themselves aren't part
// of that legislation - they come from the FRC/Companies House accounts
// taxonomy - so this mapping was built empirically, by pulling real filings
// across ~40 companies spanning tech, construction, hospitality, property,
// medical practice, farming and retail, and grouping whatever tags actually
// showed up under the statutory heading they correspond to.
//
// Different taxonomy years/company sizes tag the same line under different
// names (e.g. "cash" shows up as CashBankOnHand, CashBankInHand or
// CashCashEquivalents depending on the year) - each row lists every alias
// seen in practice, tried in order, so one tag naming choice in a given year
// doesn't make a line silently disappear.
//
// Coverage is necessarily incomplete - a micro-entity's balance sheet might
// only touch 5 of these rows, and rarer note-level tags (asset movement
// schedules, related-party balances, etc) aren't listed at all. Anything not
// covered here isn't dropped - FinancialsTable renders it in a catch-all
// "Other" section instead of forcing a guess at where it belongs.

import { FinancialsMetrics } from "../types/Financials";

export type MetricRow = {
  label: string;
  aliases: string[];
  subtotal?: boolean;
};

// Shared by FinancialsPanel (deciding which jump-links to show) and
// FinancialsTable (deciding which sections to render) so both agree on
// what's actually going to appear without duplicating the resolution logic.
export const rowsHaveData = (
  rows: MetricRow[],
  accounts: Record<string, FinancialsMetrics>
): boolean =>
  rows.some((row) =>
    Object.values(accounts).some((metrics) => row.aliases.some((alias) => metrics[alias] !== undefined))
  );

export const getOtherTags = (accounts: Record<string, FinancialsMetrics>): string[] => {
  const allTags = new Set(Object.values(accounts).flatMap((metrics) => Object.keys(metrics)));
  const mappedTags = new Set(
    [...BALANCE_SHEET_ROWS, ...PROFIT_AND_LOSS_ROWS].flatMap((row) => row.aliases)
  );
  return Array.from(allTags)
    .filter((tag) => !mappedTags.has(tag))
    .sort();
};

export const BALANCE_SHEET_ROWS: MetricRow[] = [
  // Fixed assets
  { label: "Intangible Assets", aliases: ["IntangibleAssets"] },
  { label: "Tangible Assets", aliases: ["PropertyPlantEquipment", "TangibleFixedAssets"] },
  {
    label: "Investments",
    aliases: ["InvestmentsFixedAssets", "InvestmentProperty", "InvestmentsInJointVentures"],
  },
  { label: "Fixed Assets", aliases: ["FixedAssets"], subtotal: true },

  // Current assets
  { label: "Stocks", aliases: ["TotalInventories", "StocksInventory"] },
  {
    label: "Debtors",
    aliases: [
      "Debtors",
      "TradeDebtorsTradeReceivables",
      "OtherDebtors",
      "AmountsOwedByDirectors",
      "AmountsOwedByRelatedParties",
      "AmountsOwedByGroupUndertakingsParticipatingInterests",
    ],
  },
  {
    label: "Cash at Bank and in Hand",
    aliases: ["CashBankOnHand", "CashBankInHand", "CashCashEquivalents"],
  },
  { label: "Current Asset Investments", aliases: ["CurrentAssetInvestments"] },
  { label: "Current Assets", aliases: ["CurrentAssets"], subtotal: true },

  {
    label: "Prepayments and Accrued Income",
    aliases: ["PrepaymentsAccruedIncome", "PrepaymentsAccruedIncomeNotExpressedWithinCurrentAssetSubtotal"],
  },

  {
    label: "Creditors: Amounts Falling Due Within One Year",
    aliases: [
      "CreditorsDueWithinOneYear",
      "Creditors",
      "TradeCreditorsTradePayables",
      "OtherCreditors",
      "CorporationTaxPayable",
      "OtherTaxationSocialSecurityPayable",
      "Value-addedTaxPayable",
    ],
  },

  {
    label: "Net Current Assets (Liabilities)",
    aliases: ["NetCurrentAssetsLiabilities"],
    subtotal: true,
  },
  {
    label: "Total Assets Less Current Liabilities",
    aliases: ["TotalAssetsLessCurrentLiabilities"],
    subtotal: true,
  },

  {
    label: "Creditors: Amounts Falling Due After More Than One Year",
    aliases: ["CreditorsDueAfterOneYear", "LoansFromDirectors", "TotalBorrowings"],
  },

  {
    label: "Provisions for Liabilities",
    aliases: [
      "ProvisionsForLiabilitiesBalanceSheetSubtotal",
      "ProvisionsForLiabilitiesCharges",
      "TaxationIncludingDeferredTaxationBalanceSheetSubtotal",
      "NetDeferredTaxLiabilityAsset",
    ],
  },

  {
    label: "Accruals and Deferred Income",
    aliases: [
      "AccrualsDeferredIncome",
      "AccruedLiabilitiesNotExpressedWithinCreditorsSubtotal",
      "AccruedLiabilitiesDeferredIncome",
    ],
  },

  {
    label: "Net Assets (Liabilities)",
    aliases: ["NetAssetsLiabilities", "NetAssetsLiabilitiesIncludingPensionAssetLiability", "CapitalEmployed"],
    subtotal: true,
  },

  // Capital and reserves
  {
    label: "Called Up Share Capital",
    aliases: [
      "CalledUpShareCapital",
      "ShareCapitalAllottedCalledUpPaid",
      "CalledUpShareCapitalNotPaidNotExpressedAsCurrentAsset",
    ],
  },
  { label: "Revaluation Reserve", aliases: ["RevaluationReserve"] },
  { label: "Profit and Loss Account", aliases: ["ProfitLossAccountReserve"] },
  {
    label: "Shareholders' Funds",
    aliases: ["ShareholderFunds", "Equity"],
    subtotal: true,
  },
];

export const PROFIT_AND_LOSS_ROWS: MetricRow[] = [
  { label: "Turnover", aliases: ["TurnoverRevenue"] },
  { label: "Cost of Sales", aliases: ["RawMaterialsConsumablesUsed"] },
  { label: "Gross Profit", aliases: ["GrossProfitLoss"], subtotal: true },
  { label: "Distribution Costs", aliases: ["DistributionCosts"] },
  {
    label: "Administrative Expenses",
    aliases: [
      "AdministrativeExpenses",
      "StaffCostsEmployeeBenefitsExpense",
      "DepreciationAmortisationImpairmentExpense",
      "OtherOperatingExpensesFormat2",
    ],
  },
  { label: "Other Operating Income", aliases: ["OtherOperatingIncomeFormat2"] },
  { label: "Operating Profit", aliases: ["OperatingProfitLoss"], subtotal: true },
  { label: "Interest Payable", aliases: ["InterestPayableSimilarChargesFinanceCosts"] },
  {
    label: "Profit Before Tax",
    aliases: ["ProfitLossOnOrdinaryActivitiesBeforeTax"],
    subtotal: true,
  },
  { label: "Tax", aliases: ["TaxTaxCreditOnProfitOrLossOnOrdinaryActivities"] },
  { label: "Profit for the Year", aliases: ["ProfitLoss"], subtotal: true },
];
